import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import {
  DialogComponent,
  DialogDataComponent,
  PopoverService,
  ToastFacade,
  UiPromiseService,
} from 'ngx-zcs-components';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { InstallerIdentity, RoleService } from 'ngx-core-services';

import { labels } from './enable-certified-operators.constants';
import { emailRegex, idCertificationRegex } from '../../utils/generals';

export interface CertifiedOperatorProps {
  emailRequired: string;
  emailInvalid: string;
  idCertificationRequired: string;
  enableCertifiedOperator: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
  ],
  selector: 'lib-enable-certified-operators',
  templateUrl: './enable-certified-operators.component.html',
  styleUrls: ['./enable-certified-operators.component.scss'],
})
export class EnableCertifiedOperatorsComponent {
  @Input() mode: 'mobile' | 'browser' = 'mobile';

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }

  get labels(): typeof labels{
    return this._labels;
  }

  @Output() certifiedOperatorChangeEvent = new EventEmitter<void>();

  emailPattern = emailRegex;
  idCertificationPattern = idCertificationRegex;

  certifiedOperatorProps: CertifiedOperatorProps = {
    emailRequired: this.labels.emailRequired,
    emailInvalid: this.labels.emailInvalid,
    idCertificationRequired: this.labels.idCertificationRequired,
    enableCertifiedOperator: this.labels.enableCertifiedOperator,
  };

  constructor(
    private readonly popoverService: PopoverService,
    private readonly dialog: MatDialog,
    private readonly roleService: RoleService,
    private readonly uiPromiseService: UiPromiseService,
    private readonly toastFacade: ToastFacade,
  ) {}

  async enableCertifiedOperator(form: NgForm) {
    if (form.invalid) {
      this.toastFacade.present(this.labels.formNotValid);
      return;
    }

    const email = form.controls['email'].value;
    const idCertification = Number(form.controls['idCertification'].value);

    if (this.mode === 'mobile') {
      await this.enableCertifiedMobile(email, idCertification);
    } else {
      await this.enableCertifiedBrowser(email, idCertification);
    }
  }

  async applyEnableCertified(email: string, installerId: number) {
    const entities = (await this.roleService.getAzzurroUserIdentities()) as InstallerIdentity[];
    const idAlreadyExists = entities.some((entity) => entity.codInstallatore === installerId.toString());
    if (idAlreadyExists) {
      this.toastFacade.present(this.labels.idAlreadyExists);
      return;
    }

    await this.roleService.enableCertification(email, installerId).then(() => {
      this.toastFacade.present(this.labels.registerSuccessfully);
      this.certifiedOperatorChangeEvent.emit();
      this.roleService.refresh$.next();
    }).catch(() => this.toastFacade.present(this.labels.registerFailed, undefined,{ severity: 'error' }));
  }

  async enableCertifiedMobile(email: string, installerId: number) {
    const enableCertificationPopover = await this.popoverService.simple({
      component: DialogComponent,
      componentProps: {
        name: this.labels.registerIDCertification,
        description: this.labels.registerIDCertificationDescription,
        primary: {
          click: async () => {
            await enableCertificationPopover.dismiss();
            await this.uiPromiseService.run(() => this.applyEnableCertified(email, installerId), {
              loading: true,
              loadingMessage: this.labels.loading,
            });
          },
          name: this.labels.confirm,
        },
        secondary: {
          name: this.labels.cancel,
          click: () => enableCertificationPopover.dismiss(),
        },
      },
    });
  }

  async enableCertifiedBrowser(email: string, installerId: number) {
    const dialogRef = this.dialog.open(DialogDataComponent, {
      width: '600px',
    });
    const actions = [
      {
        text: this.labels.cancel,
        action: () => dialogRef.close(),
      },
      {
        text: this.labels.registerIDCertification,
        cssClass: 'custom-mat-button-primary',
        focus: true,
        action: async () => {
          await this.uiPromiseService.run(() => this.applyEnableCertified(email, installerId), {
            loading: true,
            loadingMessage: labels.loading,
          });
          dialogRef.close();
        },
      },
    ];

    dialogRef.componentInstance.data = {
      title: this.labels.registerIDCertification,
      content: this.labels.registerIDCertificationDescription,
      buttons: actions,
    };
  }
}
